import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Divider
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { decryptText } from '../../utils/encryption';

const AssessorManagement = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [decryptedData, setDecryptedData] = useState({
    appId: null,
    equipmentId: null
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const processQRParameters = () => {
      try {
        // Get encrypted parameters from URL
        const encryptedAppId = searchParams.get('appId');
        const encryptedEquipmentId = searchParams.get('equipmentId');

        console.log('🔍 Assessor Management - Received Parameters:');
        console.log('Encrypted AppId:', encryptedAppId);
        console.log('Encrypted EquipmentId:', encryptedEquipmentId);

        if (!encryptedAppId || !encryptedEquipmentId) {
          setError('Missing required parameters. Please scan a valid QR code.');
          setLoading(false);
          return;
        }

        // Decrypt the parameters
        const decryptedAppId = decryptText(encryptedAppId);
        const decryptedEquipmentId = decryptText(encryptedEquipmentId);

        console.log('🔓 Decrypted Results:');
        console.log('AppId:', decryptedAppId);
        console.log('EquipmentId:', decryptedEquipmentId);

        if (!decryptedAppId || !decryptedEquipmentId) {
          setError('Failed to decrypt QR code parameters. Invalid QR code.');
          setLoading(false);
          return;
        }

        setDecryptedData({
          appId: decryptedAppId,
          equipmentId: decryptedEquipmentId
        });

        setLoading(false);
      } catch (error) {
        console.error('Error processing QR parameters:', error);
        setError('Error processing QR code data: ' + error.message);
        setLoading(false);
      }
    };

    processQRParameters();
  }, [searchParams]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: '#666' }}>
          Processing QR Code...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          p: 3
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <QrCodeIcon sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
            <Typography variant="h5" color="error" gutterBottom>
              QR Code Error
            </Typography>
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please try scanning the QR code again or contact support if the issue persists.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        p: 3
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 3, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            <QrCodeIcon sx={{ fontSize: '2rem', mr: 2, verticalAlign: 'middle' }} />
            Assessor Management
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Equipment Assessment and Management Portal
          </Typography>
        </Paper>

        {/* Success Alert */}
        <Alert 
          severity="success" 
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            QR Code Successfully Processed!
          </Typography>
          <Typography variant="body2">
            The equipment information has been successfully extracted from the QR code.
          </Typography>
        </Alert>

        {/* Equipment Information Card */}
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BuildIcon sx={{ fontSize: '2rem', color: '#1976d2', mr: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Equipment Information
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Application ID
                  </Typography>
                  <Chip 
                    label={decryptedData.appId}
                    color="primary"
                    size="large"
                    sx={{ fontWeight: 600, fontSize: '1rem' }}
                    icon={<AssignmentIcon />}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Equipment ID
                  </Typography>
                  <Chip 
                    label={decryptedData.equipmentId}
                    color="secondary"
                    size="large"
                    sx={{ fontWeight: 600, fontSize: '1rem' }}
                    icon={<BuildIcon />}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  elevation: 4,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <AssignmentIcon sx={{ fontSize: '3rem', color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  View Application
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access the complete application details for App ID: {decryptedData.appId}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  elevation: 4,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <BuildIcon sx={{ fontSize: '3rem', color: '#ed6c02', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Equipment Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View detailed information about Equipment ID: {decryptedData.equipmentId}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  elevation: 4,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <CheckCircleIcon sx={{ fontSize: '3rem', color: '#2e7d32', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Assessment Actions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Perform assessment activities for this equipment
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer Information */}
        <Paper sx={{ mt: 4, p: 3, backgroundColor: '#e3f2fd' }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            <strong>Note:</strong> This page was accessed via QR code scan. 
            The parameters have been automatically decrypted and processed for security.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Session Time: {new Date().toLocaleString()}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default AssessorManagement;